import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import Button from 'react-bootstrap/Button';
import config from "../../config";
import "./PavementContextCustom.css";
import ContextType from "./ContextType";
import stateContext from '../../data/state_context';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import { Modal, ToggleButton, ButtonGroup } from "react-bootstrap";
import SideNav from "../SideNav/SideNav";
import HelpIcon from "../HelpIcon/HelpIcon";
import useFormValidation from "../../hooks/useFormValidation";
import ValidationError from "../ValidationError/ValidationError";

const PavementContextCustom = () => {
    let { id } = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const selectedState = queryParams.get('state');

    let navigate = useNavigate();
    const { handleBlur, getFieldState, validateAll } = useFormValidation();
    const [data, setData] = useState(ContextType);
    const [error, setError] = useState(null);
    const [errorPopupShow, setErrorPopupShow] = useState(false);
    const [validationErrorShow, setValidationErrorShow] = useState(false);
    const [unitSystem, setUnitSystem] = useState('imperial'); // 'metric' or 'imperial'

    // Conversion factor: 1 mph = 1.60934 km/h
    const conversionFactors = {
        mph_to_kmh: 1.60934
    };

    const toggleUnitSystem = () => {
        setUnitSystem(prev => prev === 'metric' ? 'imperial' : 'metric');
    };

    // Convert from storage units (mph) to display units
    const displayValue = (value, unitType) => {
        if (unitSystem === 'imperial') {
            return value; // No conversion needed - backend stores mph
        }
        // Metric: convert mph to km/h
        if (unitType === 'speed') {
            return value * conversionFactors.mph_to_kmh;
        }
        return value;
    };

    // Convert from display units back to storage units (mph)
    const inverseDisplayValue = (value, unitType) => {
        if (unitSystem === 'imperial') {
            return value; // No conversion needed
        }
        // Metric: convert km/h back to mph
        if (unitType === 'speed') {
            return value / conversionFactors.mph_to_kmh;
        }
        return value;
    };

    useEffect(() => {
        axios.get(`${config.apiUrl}/api/context/${id}`)
            .then(response => {
                if (response.data.state !== selectedState) {
                    console.log(response.data)
                    setData(response.data);
                } else {
                    setData(stateContext[selectedState]);
                    setData(prevData => {
                        return { ...prevData, id: response.data.id };
                    });
                }
            })
            .catch(error => {
                setError(error.message);
                handleErrorPopupShow();
            });
    }, [id, selectedState]);

    if (error) console.log(error);

    function handleChange(e) {
        const { name, value, type, checked } = e.target;
        const unitType = e.target.dataset?.unitType; // Get unit type from data attribute
        const nameParts = name.split('.');  // Split the name by dots to handle nested keys, e.g., 'user.name'

        // Convert from display units to storage units if needed
        let storageValue = value;
        if (unitType && type === 'number' && value !== '') {
            storageValue = inverseDisplayValue(parseFloat(value), unitType);
        }

        setData(prevFormData => {
            // Function to recursively update nested objects
            const updateNestedObject = (obj, parts, val) => {
                const part = parts.shift();  // Get the next property name in the path
                if (parts.length > 0) {
                    obj[part] = updateNestedObject(obj[part] || {}, parts, val);
                } else {
                    obj[part] = type === 'checkbox' ? checked : val;  // Set the value for the last part of the path
                }
                return { ...obj };
            };

            return updateNestedObject({ ...prevFormData }, nameParts, storageValue);
        });
    }

    const goToBasicContext = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/pavement-context-basic/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPavementDesign = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/pavement-design/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToPedegreeMatrix = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/pedegree-matrix/${id}?backref=pavement-context-custom`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToAsphaltEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/asphalt-embodied-emission/${id}?backref=pavement-context-custom`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();
        });
    }

    const goToConcreteEmbodiedEmission = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/concrete-embodied-emission/${id}?backref=pavement-context-custom`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();

        });
    }

    const goToResults = () => {
        if (!validateForm()) {
            setValidationErrorShow(true);
            return;
        }
        axios.put(`${config.apiUrl}/api/context/${data.id}`, data).then(response => {
            navigate(`/report/${id}`);
        }).catch(error => {
            setError(error.message);
            handleErrorPopupShow();

        });
    }

    const handleErrorPopupClose = () => setErrorPopupShow(false);
    const handleErrorPopupShow = () => setErrorPopupShow(true);

    // Validate all form fields before submission
    const validateForm = () => {
        const fields = [
            { name: 'aadtPerLane.min', value: data.aadtPerLane.min, type: 'number' },
            { name: 'aadtPerLane.mean', value: data.aadtPerLane.mean, type: 'number' },
            { name: 'aadtPerLane.max', value: data.aadtPerLane.max, type: 'number' },
            { name: 'aadtAllLane.min', value: data.aadtAllLane.min, type: 'number' },
            { name: 'aadtAllLane.mean', value: data.aadtAllLane.mean, type: 'number' },
            { name: 'aadtAllLane.max', value: data.aadtAllLane.max, type: 'number' },
            { name: 'truckPercentage.min', value: data.truckPercentage.min, type: 'number' },
            { name: 'truckPercentage.mean', value: data.truckPercentage.mean, type: 'number' },
            { name: 'truckPercentage.max', value: data.truckPercentage.max, type: 'number' },
            { name: 'aadttPerLane.min', value: data.aadttPerLane.min, type: 'number' },
            { name: 'aadttPerLane.mean', value: data.aadttPerLane.mean, type: 'number' },
            { name: 'aadttPerLane.max', value: data.aadttPerLane.max, type: 'number' },
            { name: 'aadttAllLane.min', value: data.aadttAllLane.min, type: 'number' },
            { name: 'aadttAllLane.mean', value: data.aadttAllLane.mean, type: 'number' },
            { name: 'aadttAllLane.max', value: data.aadttAllLane.max, type: 'number' },
            { name: 'trafficGrowth.min', value: data.trafficGrowth.min, type: 'number' },
            { name: 'trafficGrowth.mean', value: data.trafficGrowth.mean, type: 'number' },
            { name: 'trafficGrowth.max', value: data.trafficGrowth.max, type: 'number' },
            { name: 'trafficSpeed.min', value: data.trafficSpeed.min, type: 'number' },
            { name: 'trafficSpeed.mean', value: data.trafficSpeed.mean, type: 'number' },
            { name: 'trafficSpeed.max', value: data.trafficSpeed.max, type: 'number' },
            { name: 'reliability.min', value: data.reliability.min, type: 'number' },
            { name: 'reliability.mean', value: data.reliability.mean, type: 'number' },
            { name: 'reliability.max', value: data.reliability.max, type: 'number' },
        ];
        return validateAll(fields);
    };

    return (
        <>
            <SideNav active="pavement-context-custom" id={id} passed={["pavement-context-basic"]}
                goToBasicContext={goToBasicContext}
                goToDetailedContext={() => { }}
                goToAsphaltEmbodiedEmission={goToAsphaltEmbodiedEmission}
                goToConcreteEmbodiedEmission={goToConcreteEmbodiedEmission}
                goToPedegreeMatrix={goToPedegreeMatrix}
                goToPavementDesign={goToPavementDesign}
                goToResults={goToResults}
            />
            <Container>
            <Row>
                <Col>
                    <div className="pcb__sub">
                        <div className="pcb__box" style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "20px" }}>
                            <div>
                                <div style={{ fontWeight: "bold", marginBottom: "5px" }}>Unit Toggle</div>
                                <ButtonGroup size="sm">
                                    <ToggleButton
                                        id="unit-toggle-metric"
                                        type="checkbox"
                                        variant={unitSystem === 'metric' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'metric'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Metric
                                    </ToggleButton>
                                    <ToggleButton
                                        id="unit-toggle-imperial"
                                        type="checkbox"
                                        variant={unitSystem === 'imperial' ? 'primary' : 'outline-primary'}
                                        checked={unitSystem === 'imperial'}
                                        onChange={toggleUnitSystem}
                                    >
                                        Imperial
                                    </ToggleButton>
                                </ButtonGroup>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__sub">
                        <h3 className="pcb__sub-title">Context Parameters</h3>
                        <div className="pcb__box">
                            <table className="pcc__table">
                                <thead>
                                    <tr>
                                        <th className="pcc__table-header">Parameters</th>
                                        <th className="pcc__table-header">Min.</th>
                                        <th className="pcc__table-header">Mean</th>
                                        <th className="pcc__table-header">Max.</th>
                                        <th className="pcc__table-header">
                                            Distribution
                                            <HelpIcon text="Uniform: Equal probability across range. Normal: Bell curve, values cluster around mean. Lognormal: Right-skewed, no negative values. Triangular: Peak at most likely value." />
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* AADT per line */}
                                    <tr>
                                        <td className="pcc__table-label">AADT per Lane</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadtPerLane.min"
                                                value={data.aadtPerLane.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtPerLane.min').isInvalid} />
                                            <ValidationError message={getFieldState('aadtPerLane.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadtPerLane.mean"
                                                value={data.aadtPerLane.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtPerLane.mean').isInvalid} />
                                            <ValidationError message={getFieldState('aadtPerLane.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                className="pcc__input"
                                                name="aadtPerLane.max"
                                                value={data.aadtPerLane.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtPerLane.max').isInvalid} />
                                            <ValidationError message={getFieldState('aadtPerLane.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="aadtPerLane.distribution"
                                                value={data.aadtPerLane.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* AADT All Lane */}
                                    <tr>
                                        <td className="pcc__table-label">AADT All Lane</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadtAllLane.min"
                                                value={data.aadtAllLane.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtAllLane.min').isInvalid} />
                                            <ValidationError message={getFieldState('aadtAllLane.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadtAllLane.mean"
                                                value={data.aadtAllLane.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtAllLane.mean').isInvalid} />
                                            <ValidationError message={getFieldState('aadtAllLane.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadtAllLane.max"
                                                value={data.aadtAllLane.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadtAllLane.max').isInvalid} />
                                            <ValidationError message={getFieldState('aadtAllLane.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="aadtAllLane.distribution"
                                                id="state"
                                                value={data.aadtAllLane.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>



                                    {/* Truck Percentage (%) */}
                                    <tr>
                                        <td className="pcc__table-label">Truck Percentage (%)</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="truckPercentage.min"
                                                value={data.truckPercentage.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('truckPercentage.min').isInvalid} />
                                            <ValidationError message={getFieldState('truckPercentage.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="truckPercentage.mean"
                                                value={data.truckPercentage.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('truckPercentage.mean').isInvalid} />
                                            <ValidationError message={getFieldState('truckPercentage.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="truckPercentage.max"
                                                value={data.truckPercentage.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('truckPercentage.max').isInvalid} />
                                            <ValidationError message={getFieldState('truckPercentage.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="truckPercentage.distribution"
                                                value={data.truckPercentage.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>

                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* AADTT per Lane */}
                                    <tr>
                                        <td className="pcc__table-label">AADTT per Lane</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttPerLane.min"
                                                value={data.aadttPerLane.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttPerLane.min').isInvalid} />
                                            <ValidationError message={getFieldState('aadttPerLane.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttPerLane.mean"
                                                value={data.aadttPerLane.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttPerLane.mean').isInvalid} />
                                            <ValidationError message={getFieldState('aadttPerLane.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttPerLane.max"
                                                value={data.aadttPerLane.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttPerLane.max').isInvalid} />
                                            <ValidationError message={getFieldState('aadttPerLane.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="aadttPerLane.distribution"
                                                value={data.aadttPerLane.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* AADTT All Lane */}
                                    <tr>
                                        <td className="pcc__table-label">AADTT All Lane</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttAllLane.min"
                                                value={data.aadttAllLane.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttAllLane.min').isInvalid} />
                                            <ValidationError message={getFieldState('aadttAllLane.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttAllLane.mean"
                                                value={data.aadttAllLane.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttAllLane.mean').isInvalid} />
                                            <ValidationError message={getFieldState('aadttAllLane.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="aadttAllLane.max"
                                                value={data.aadttAllLane.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('aadttAllLane.max').isInvalid} />
                                            <ValidationError message={getFieldState('aadttAllLane.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="aadttAllLane.distribution"
                                                value={data.aadttAllLane.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* Traffic Growth (%) */}
                                    <tr>
                                        <td className="pcc__table-label">Traffic Growth (%)</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="trafficGrowth.min"
                                                value={data.trafficGrowth.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('trafficGrowth.min').isInvalid} />
                                            <ValidationError message={getFieldState('trafficGrowth.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="trafficGrowth.mean"
                                                value={data.trafficGrowth.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('trafficGrowth.mean').isInvalid} />
                                            <ValidationError message={getFieldState('trafficGrowth.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="trafficGrowth.max"
                                                value={data.trafficGrowth.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('trafficGrowth.max').isInvalid} />
                                            <ValidationError message={getFieldState('trafficGrowth.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="trafficGrowth.distribution"
                                                value={data.trafficGrowth.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* Traffic Speed */}
                                    <tr>
                                        <td className="pcc__table-label">Traffic Speed ({unitSystem === 'imperial' ? 'mph' : 'km/h'})</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                step="0.01"
                                                style={{ width: "95%" }}
                                                name="trafficSpeed.min"
                                                data-unit-type="speed"
                                                value={data.trafficSpeed.min === '' ? '' : parseFloat(displayValue(Number(data.trafficSpeed.min ?? 0), 'speed').toFixed(2))}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, data.trafficSpeed.min, 'number')}
                                                isInvalid={getFieldState('trafficSpeed.min').isInvalid} />
                                            <ValidationError message={getFieldState('trafficSpeed.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                step="0.01"
                                                style={{ width: "95%" }}
                                                name="trafficSpeed.mean"
                                                data-unit-type="speed"
                                                value={data.trafficSpeed.mean === '' ? '' : parseFloat(displayValue(Number(data.trafficSpeed.mean ?? 0), 'speed').toFixed(2))}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, data.trafficSpeed.mean, 'number')}
                                                isInvalid={getFieldState('trafficSpeed.mean').isInvalid} />
                                            <ValidationError message={getFieldState('trafficSpeed.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                step="0.01"
                                                style={{ width: "95%" }}
                                                name="trafficSpeed.max"
                                                data-unit-type="speed"
                                                value={data.trafficSpeed.max === '' ? '' : parseFloat(displayValue(Number(data.trafficSpeed.max ?? 0), 'speed').toFixed(2))}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, data.trafficSpeed.max, 'number')}
                                                isInvalid={getFieldState('trafficSpeed.max').isInvalid} />
                                            <ValidationError message={getFieldState('trafficSpeed.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="trafficSpeed.distribution"
                                                value={data.trafficSpeed.distribution}
                                                onChange={handleChange}>
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>

                                    {/* Reliability (%) */}
                                    <tr>
                                        <td className="pcc__table-label">Reliability (%)</td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="reliability.min"
                                                value={data.reliability.min}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('reliability.min').isInvalid} />
                                            <ValidationError message={getFieldState('reliability.min').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="reliability.mean"
                                                value={data.reliability.mean}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('reliability.mean').isInvalid} />
                                            <ValidationError message={getFieldState('reliability.mean').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Control
                                                type="number"
                                                size="sm"
                                                style={{ width: "95%" }}
                                                name="reliability.max"
                                                value={data.reliability.max}
                                                onChange={handleChange}
                                                onBlur={(e) => handleBlur(e.target.name, e.target.value, 'number')}
                                                isInvalid={getFieldState('reliability.max').isInvalid} />
                                            <ValidationError message={getFieldState('reliability.max').errorMessage} />
                                        </td>
                                        <td className="pcc__table-element">
                                            <Form.Select
                                                size="sm"
                                                style={{ width: "250px" }}
                                                name="reliability.distribution"
                                                value={data.reliability.distribution}
                                                onChange={handleChange} >
                                                <option value="Uniform" selected>Uniform</option>
                                                <option value="Normal">Normal</option>
                                                <option value="Lognormal">Lognormal</option>
                                                <option value="Triangular">Triangular</option>
                                            </Form.Select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className="pcb__box-footer-right">
                        {/* <Button variant="primary" onClick={goToPedegreeMatrix} >Pedegree Matrix</Button>
                        <Button variant="primary" onClick={goToConcreteEmbodiedEmission}>Concrete Embodied Emission</Button>
                        <Button variant="primary" onClick={goToAsphaltEmbodiedEmission}>Asphalt Embodied Emission</Button>
                        <Button variant="primary" href={`/pavement-context-basic/${id}`}>Basic Input</Button> */}
                        <Button variant="primary" onClick={goToConcreteEmbodiedEmission}>Save and Next</Button>
                    </div>

                </Col>
            </Row>

            <Modal show={errorPopupShow} onHide={handleErrorPopupClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>{error}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleErrorPopupClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={validationErrorShow} onHide={() => setValidationErrorShow(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Validation Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>Please fix the highlighted errors before continuing. All fields are required and number values must be greater than 0.</Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setValidationErrorShow(false)}>
                        OK
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
        </>
    );
};

export default PavementContextCustom;